import { observable, configure, action, computed } from 'mobx';
import LocalModel from './localModel';

import util from 'preact-util';
import { route } from 'preact-router';
import PubSub, { topics } from '../lib/pubsub';
import localUtil from '../lib/util';
import { resolveSkillClass, resolveWeaponDisplay, weaponAbbr, expectedStandsForStage, getStageTypeDisplay, perStandScore, rowTotals, isPerfectSoFar, rowLossSummary, resolveStandConfig, buildScoreTeamSections } from '../lib/conventionResults';

configure({ enforceActions: 'always' });

const T96_SERIES = [
    // 11 m — alternating Fri / 1 hånd at 150s, 20s, 10s
    { idx: 1,  distance: '11m', timing: '150 s', grip: 'Fri'    },
    { idx: 2,  distance: '11m', timing: '150 s', grip: '1 hånd' },
    { idx: 3,  distance: '11m', timing: '20 s',  grip: 'Fri'    },
    { idx: 4,  distance: '11m', timing: '20 s',  grip: '1 hånd' },
    { idx: 5,  distance: '11m', timing: '10 s',  grip: 'Fri'    },
    { idx: 6,  distance: '11m', timing: '10 s',  grip: '1 hånd' },
    // 15 m — same shape as 11 m
    { idx: 7,  distance: '15m', timing: '150 s', grip: 'Fri'    },
    { idx: 8,  distance: '15m', timing: '150 s', grip: '1 hånd' },
    { idx: 9,  distance: '15m', timing: '20 s',  grip: 'Fri'    },
    { idx: 10, distance: '15m', timing: '20 s',  grip: '1 hånd' },
    { idx: 11, distance: '15m', timing: '10 s',  grip: 'Fri'    },
    { idx: 12, distance: '15m', timing: '10 s',  grip: '1 hånd' },
    // 25 m — all Fri
    { idx: 13, distance: '25m', timing: '150 s', grip: 'Fri'    },
    { idx: 14, distance: '25m', timing: '150 s', grip: 'Fri'    },
    { idx: 15, distance: '25m', timing: '20 s',  grip: 'Fri'    },
    { idx: 16, distance: '25m', timing: '20 s',  grip: 'Fri'    },
];

const T96_MAX_PER_SERIES = { scoreHits: 6, scoreTargets: 5, scoreInner: 6 };

const T96_DISTANCE_GROUPS = [
    { distance: '11m', startIdx: 1,  endIdx: 6  },
    { distance: '15m', startIdx: 7,  endIdx: 12 },
    { distance: '25m', startIdx: 13, endIdx: 16 },
];

class ConventionResultStore extends LocalModel {
    constructor() {
        super('conventionResult', {
            namePlural: 'conventionResults',
            namePluralReal: 'conventionResults',
            sort: '-createdDate',
            // Default ceiling for any load() that omits an explicit limit. Kept high
            // (50000) because per-convention loads pull every Lag's result rows; a lower
            // default sorted -createdDate silently drops the OLDEST rows (earliest Lags),
            // leaving their scoring grids empty on large conventions.
            limit: 50000,
            api: {
                search: {
                    url: '/api/conventionresults/',
                    params: {
                        limit: 15,
                        sort: '-createdDate',
                    },
                },
                load: {
                    url: '/api/conventionresults/',
                    params: {},
                },
                save: {
                    url: '/api/conventionresults/',
                    params: {},
                },
                delete: {
                    url: '/api/conventionresults/',
                    params: {},
                },
            },
        });
    }

    @observable newConventionResult = {};

    @observable conventionResult = {};

    @observable conventionResults = [];

    @observable tags = [];

    @observable rankings = {
        year: null,
        availableYears: [],
        customer: null,
        rows: [],
    };

    @action
    updateResultLocal({ id, participant, convention, stage, stand, targetIndex, senderId, ...fields }) {
        // Live broadcasts are display-only, but they must still never smear one
        // shooter's score onto another's row. Require a full cell identity, and
        // never trust a bare id: when an id-match disagrees with the payload's
        // own identity (stale id cache, buggy or hostile sender) the payload is
        // dropped instead of applied — the periodic authoritative reload
        // restores any legitimately missed update. senderId is destructured out
        // above so broadcast plumbing never lands on a score row.
        if (!participant || !stage) {
            return;
        }
        let existingIdx = -1;

        // First try to find by ID if provided (most reliable)
        if (id) {
            existingIdx = this.conventionResults.findIndex(result => result.id === id);
            if (existingIdx >= 0) {
                const row = this.conventionResults[existingIdx];
                const sameCell = stand !== undefined
                    ? row.stand === stand
                    : row.targetIndex === targetIndex;
                if (row.participant !== participant || row.stage !== stage || !sameCell) {
                    return;
                }
            }
        }

        // If not found by ID, try to find by participant/stage/stand or targetIndex
        if (existingIdx < 0) {
            existingIdx = this.conventionResults.findIndex(result =>
                result.participant === participant &&
                result.convention === convention &&
                result.stage === stage &&
                (stand !== undefined ? result.stand === stand : result.targetIndex === targetIndex)
            );
        }

        if (existingIdx >= 0) {
            // Update existing result
            const existingResult = this.conventionResults[existingIdx];
            Object.keys(fields).forEach(key => {
                existingResult[key] = fields[key];
            });
            if (id && !existingResult.id) {
                existingResult.id = id;
            }
        } else {
            // Add new result (only if we don't have an ID - truly new)
            // Don't set id - leave it undefined so data-id attribute stays empty
            const newResult = {
                participant,
                convention,
                stage,
                stand,
                targetIndex,
                ...fields,
            };
            if (id) {
                newResult.id = id;
            }
            this.conventionResults.push(newResult);
        }
    }

    @action
    updateResultId({ participant, convention, stage, stand, targetIndex, newId }) {
        // Update temporary ID with real ID from server
        const existingIdx = this.conventionResults.findIndex(result =>
            result.participant === participant &&
            result.convention === convention &&
            result.stage === stage &&
            (stand !== undefined ? result.stand === stand : result.targetIndex === targetIndex)
        );

        if (existingIdx >= 0) {
            this.conventionResults[existingIdx].id = newId;
        }
    }

    async loadScoreAudit({ convention, stage, stand }) {
        const response = await util.fetchApi(`/api/conventions/${convention}/score-audit`, { method: 'GET' }, { stage, stand });
        if (response && response.status === 200) {
            return {
                data: response.data || [],
                users: (response.included && response.included.users) || [],
            };
        }
        return { data: [], users: [] };
    }

    get currentHostname() {
        return typeof window !== 'undefined' ? window.location.hostname : '';
    }

    async loadRankings({ year } = {}) {
        const response = await util.fetchApi('/api/conventions/rankings', { method: 'GET' }, {
            hostname: this.currentHostname,
            year,
        });
        if (response && response.status === 200 && response.data) {
            this.updateKeyValue('rankings', response.data);
        }
        return response;
    }

    generateArray(X) {
        const myNewShinyArray = [...Array(parseInt(X, 10) + 1).keys()];
        myNewShinyArray.shift();
        return myNewShinyArray;
    }

    getT96Series() {
        return T96_SERIES;
    }

    getT96DistanceGroups() {
        return T96_DISTANCE_GROUPS;
    }

    getT96MaxPerSeries() {
        return T96_MAX_PER_SERIES;
    }

    isT96(type) {
        return type === 't-96';
    }

    getParticipantFields(convention) {
        if (convention.type === 'standard') {
            return ['150s', '150s', '150s', '150s', '20s', '20s', '20s', '20s', '10s', '10s', '10s', '10s'];
        }
        if (convention.type === 'hurtig') {
            return ['10s', '10s', '10s', '10s', '8s', '8s', '8s', '8s', '6s', '6s', '6s', '6s'];
        }
        if (convention.type === 'hurtig-half') {
            return ['10s', '10s', '8s', '8s', '6s', '6s'];
        }
        if (convention.type === 'nais') {
            return ['150s', '150s', '5x3s', '5x3s', '20s', '10s'];
        }
        if (convention.type === 'norgesfelt') {
            return ['Treff', 'Skvr', 'iX', 'Trekk'];
        }
        if (convention.type === 'felt') {
            return ['Treff', 'Skvr', 'iX', 'Trekk'];
        }
        if (convention.type === 't-96') {
            return T96_SERIES.map(s => `${s.timing} ${s.grip}`);
        }
        // VM-Finpistol & VM-Grovpistol: 60 shots (30 precision + 30 duel)
        // 6 precision series + 6 duel series
        if (convention.type === 'finpistol' || convention.type === 'grovpistol') {
            return ['P1', 'P2', 'P3', 'P4', 'P5', 'P6', 'D1', 'D2', 'D3', 'D4', 'D5', 'D6'];
        }
        // Silhuettpistol: 60 shots in 12 series (4x8s, 4x6s, 4x4s)
        if (convention.type === 'silhuett') {
            return ['8s', '8s', '8s', '8s', '6s', '6s', '6s', '6s', '4s', '4s', '4s', '4s'];
        }
        // Luftpistol: 60 precision shots in 6 series of 10
        if (convention.type === 'luftpistol') {
            return ['S1', 'S2', 'S3', 'S4', 'S5', 'S6'];
        }
        // Skarpskyttermerke: 21 shots in 4 exercises
        if (convention.type === 'skarpskytter') {
            return ['Øv1', 'Øv2', 'Øv3', 'Øv4'];
        }
        // Fri (poeng): one points box per stand, count from the stage's "Number of stands" (default 6)
        if (convention.type === 'fri') {
            const standCount = parseInt(convention.stages, 10) > 0 ? parseInt(convention.stages, 10) : 6;
            return Array.from({ length: standCount }, (_, i) => `P${i + 1}`);
        }
        // Tidsøvelse: 10 stands with time recordings
        if (convention.type === 'tidsoving') {
            return ['T1', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'T8', 'T9', 'T10'];
        }
        // Tidskonkurranse: 3 stages, lowest time wins
        if (convention.type === 'tidskonk') {
            return ['Tid1', 'Tid2', 'Tid3'];
        }
        return [];
    }

    isTimeStage(type) {
        return ['tidsoving', 'tidskonk'].includes(type);
    }

    getNorgesfeltTargetMaxValues() {
        return [
            { stand: 1, scoreHits: 6, scoreGroups: 1, scoreTargets: 2, scoreInner: 6 },
            { stand: 2, scoreHits: 6, scoreGroups: 2, scoreTargets: 6, scoreInner: 6 },
            { stand: 3, scoreHits: 6, scoreGroups: 2, scoreTargets: 5, scoreInner: 6 },
            { stand: 4, scoreHits: 6, scoreGroups: 1, scoreTargets: 3, scoreInner: 6 },
            { stand: 5, scoreHits: 6, scoreGroups: 1, scoreTargets: 2, scoreInner: 6 },
            { stand: 6, scoreHits: 6, scoreGroups: 2, scoreTargets: 5, scoreInner: 6 },
            { stand: 7, scoreHits: 6, scoreGroups: 1, scoreTargets: 3, scoreInner: 6 },
            { stand: 8, scoreHits: 6, scoreGroups: 2, scoreTargets: 6, scoreInner: 6 },
        ];
    }

    isFieldShooting(type) {
        return ['norgesfelt', 'felt', 't-96'].includes(type);
    }

    isTimeCompetitionType(type) {
        return type === 'tidskonk';
    }

    getResultsListForAllClasses(convention, results, participants = [], classMap = [], disciplineMap = []) {
        const allResults = {};

        if (!convention || !convention.stages || !convention.stages.length) {
            return allResults;
        }


        const generateArray = (X) => {
            const arr = [...Array(parseInt(X, 10) + 1).keys()];
            arr.shift();
            return arr;
        };

        convention.stages.forEach((stage, idx) => {
            // Resultatliste only includes stages explicitly marked done.
            // Paused (stageClosed) lags are still ongoing and live in Pågående.
            if (!parseInt(stage.stageDone, 10)) return;

            const isFieldShootingStage = this.isFieldShooting(stage.type);
            const isT96Stage = this.isT96(stage.type);
            const isTimeCompetition = this.isTimeCompetitionType(stage.type);
            const stageParticipants = convention[`participants${idx + 1}`];
            const stands = generateArray(stage.stages || 10);
            const stageFields = this.getParticipantFields(stage);

            if (stageParticipants && stageParticipants.length) {
                stageParticipants.forEach(participant => {
                    const participantResults = results.filter(result => result.participant === participant.id && result.stage === stage.md5);
                    let totalScore = 0;
                    let totalIx = 0;
                    let standScore = 0;
                    let maxHits = 0;
                    let maxTargets = 0;
                    let maxInner = 0;
                    let allTimes = [];

                    const weaponClass = participant.class || 'Ingen klasse';
                    const isTimeStage = this.isTimeStage(stage.type);
                    const currentParticipant = participants.find(p => p.id === participant.id);
                    const skillClass = resolveSkillClass(participant, currentParticipant, stage, classMap, disciplineMap);
                    const skillClassKey = skillClass === null ? '_none' : skillClass;
                    const resultKey = `${stage.type}::${weaponClass}::::open::${skillClassKey}`;
                    const sectionKey = `${stage.type}::${weaponClass}::::open`;
                    if (!allResults[resultKey]) {
                        allResults[resultKey] = {
                            className: weaponClass,        // kept for backward compat with any consumer
                            weaponClass,
                            weaponDisplay: resolveWeaponDisplay(weaponClass),
                            skillClass,                    // null for Norgesfelt
                            sectionKey,
                            stageType: stage.type,
                            stageName: stage.name || stage.type,
                            isTimeCompetition,
                            isTimeStage,
                            championship: '',
                            ageGroup: 'open',
                            participants: [],
                        };
                    }

                    allResults[resultKey].participants.push({
                        id: participant.id,
                        name: localUtil.displayName(participant),
                        sportsClub: currentParticipant ? currentParticipant.sportsClub || 'Mangler klubb' : 'n/a',
                        nsfId: currentParticipant ? currentParticipant.nsfId : null,
                        rubicId: currentParticipant ? currentParticipant.rubicId : null,
                        nsfDiciplineClasses: currentParticipant ? currentParticipant.nsfDiciplineClasses : [],
                        stageMd5: stage.md5,
                        // '' (ranked) | 'dnf' | 'dns' | 'dsq'. Statused shooters sort last and
                        // show their code instead of a rank/score in the results list.
                        resultStatus: (participant.resultStatus || '').toString().toLowerCase(),
                        totalScore: 0,
                        totalIx: 0,
                        bestTime: null,
                        results: [],
                    });
                    const participantResult = allResults[resultKey].participants[allResults[resultKey].participants.length - 1];

                    if (isT96Stage) {
                        T96_SERIES.forEach(series => {
                            const stand = series.idx;
                            const targetResult = participantResults.find(r => r.stand === stand);
                            const seriesHits    = targetResult?.scoreHits    || 0;
                            const seriesTargets = targetResult?.scoreTargets || 0;
                            const seriesInner   = targetResult?.scoreInner   || 0;
                            const seriesScore   = seriesHits + seriesTargets + seriesInner;

                            totalScore += seriesScore;
                            totalIx    += seriesInner;

                            participantResult.results.push({
                                stand,
                                scoreHits:    seriesHits,
                                scoreTargets: seriesTargets,
                                scoreInner:   seriesInner,
                                scorePenalty: 0,
                                scoreTotal:   seriesScore,
                            });
                        });
                        maxHits    = T96_MAX_PER_SERIES.scoreHits    * T96_SERIES.length; // 96
                        maxTargets = T96_MAX_PER_SERIES.scoreTargets * T96_SERIES.length; // 80
                        maxInner   = T96_MAX_PER_SERIES.scoreInner   * T96_SERIES.length; // 96
                    } else if (isFieldShootingStage) {
                        stands.forEach(stand => {
                            standScore = 0;
                            const targetResult = participantResults.find(result => result.stand === stand);
                            standScore += targetResult?.scoreHits || 0;
                            standScore += targetResult?.scoreTargets || 0;
                            if (targetResult?.scorePenalty) {
                                standScore -= targetResult.scorePenalty;
                            }
                            totalScore += standScore;
                            totalIx += targetResult?.scoreInner || 0;
                            participantResult.results.push({
                                stand,
                                scoreHits: targetResult?.scoreHits || 0,
                                scoreTargets: targetResult?.scoreTargets || 0,
                                scorePenalty: targetResult?.scorePenalty || 0,
                                scoreInner: targetResult?.scoreInner || 0,
                                scoreTotal: standScore,
                            });

                            // Effective per-stand max for this shooter's class — honours
                            // the per-class config (sp/magnum) and the 5-shot cap fallback.
                            const standCfg = resolveStandConfig(stage, stand, convention, participant.class);
                            maxHits += standCfg.scoreHits;
                            maxTargets += standCfg.scoreTargets;
                            maxInner += standCfg.scoreInner;
                        });
                    } else {
                        stageFields.forEach((field, index) => {
                            const targetResult = participantResults.find(result => result.targetIndex === index);
                            standScore = isTimeStage
                                ? (parseFloat(targetResult?.scoreTotal) || 0)
                                : parseInt(targetResult?.scoreTotal || 0, 10);
                            totalScore += standScore;
                            if (isTimeCompetition && standScore > 0) {
                                allTimes.push(standScore);
                            }
                            participantResult.results.push({
                                stand: index,
                                scoreHits: 0,
                                scoreTargets: 0,
                                scorePenalty: 0,
                                scoreInner: 0,
                                scoreTotal: standScore,
                            });
                        });
                    }

                    if (isTimeCompetition && allTimes.length > 0) {
                        participantResult.bestTime = Math.min(...allTimes);
                        participantResult.totalScore = participantResult.bestTime;
                    } else {
                        participantResult.totalScore = totalScore;
                    }
                    participantResult.totalIx = totalIx;
                    const maxTotal = isT96Stage
                        ? maxHits + maxTargets + maxInner
                        : maxHits + maxTargets;
                    participantResult.rankingScore = maxTotal > 0 ? (totalScore / maxTotal * 100).toFixed(1) : '0.0';

                    allResults[resultKey].participants[allResults[resultKey].participants.length - 1] = participantResult;
                    if (isTimeCompetition) {
                        allResults[resultKey].participants.sort(localUtil.fieldSorter(['resultStatus', 'totalScore', 'name']));
                    } else {
                        allResults[resultKey].participants.sort(localUtil.fieldSorter(['resultStatus', '-totalScore', '-totalIx']));
                    }

                    // Build duplicate buckets for KM / DM championships and age categories.
                    // Bucket assignment follows NSF official classes (see NM Felt 2025).
                    // Each participant lands in exactly ONE bucket per championship:
                    //   ageClass='u'  → "u"  (Klasse U, under-16 youth — no gender split)
                    //   ageClass='jr' + gender='k' → "jk"
                    //   ageClass='jr' + gender='m' → "jm"
                    //   ageClass='jr' + no gender → "jr" (combined junior 16–20)
                    //   ageClass='v55'/'v65'/'v73' → that veteran bucket (no gender split)
                    //   Adults (no ageClass) → "k" or "m" by gender, else "open"
                    // For SP/Magnum weapons (sp.pistol, sp.rev, mag1, mag2): only "u",
                    // "jr" or "open" — no gender splits and no veteran buckets.
                    const SP_MAGNUM = ['sp.pistol', 'sp.rev', 'mag1', 'mag2'];
                    const isSpMagnum = SP_MAGNUM.includes(weaponClass);
                    const rawAge = (participant.ageClass || '').toString().trim().toLowerCase();
                    const ageGroup = ['u', 'jr', 'v55', 'v65', 'v73'].includes(rawAge) ? rawAge : '';
                    const rawGender = (participant.gender || '').toString().trim().toLowerCase();
                    const gender = ['k', 'm'].includes(rawGender) ? rawGender : '';
                    let ageBuckets;
                    if (isSpMagnum) {
                        if (ageGroup === 'u') ageBuckets = ['u'];
                        else if (ageGroup === 'jr') ageBuckets = ['jr'];
                        else ageBuckets = ['open'];
                    } else if (ageGroup === 'u') {
                        ageBuckets = ['u'];
                    } else if (ageGroup === 'jr') {
                        if (gender === 'k') ageBuckets = ['jk'];
                        else if (gender === 'm') ageBuckets = ['jm'];
                        else ageBuckets = ['jr'];
                    } else if (ageGroup) {
                        ageBuckets = [ageGroup];
                    } else if (gender === 'k') {
                        ageBuckets = ['k'];
                    } else if (gender === 'm') {
                        ageBuckets = ['m'];
                    } else {
                        ageBuckets = ['open'];
                    }
                    const isKM = !!parseInt(participant.isKM, 10);
                    const isDM = !!parseInt(participant.isDM, 10);
                    const championshipsToAdd = [];
                    if (isKM) championshipsToAdd.push('KM');
                    if (isDM) championshipsToAdd.push('DM');
                    championshipsToAdd.forEach(championship => {
                        ageBuckets.forEach(grp => {
                            // Championship medals (KM/DM) are decided per weapon + age
                            // group ACROSS skill classes — never split into A/B/C/D
                            // tables like the open list. One combined bucket per section.
                            const champResultKey = `${stage.type}::${weaponClass}::${championship}::${grp}::_none`;
                            const champSectionKey = `${stage.type}::${weaponClass}::${championship}::${grp}`;
                            if (!allResults[champResultKey]) {
                                allResults[champResultKey] = {
                                    className: weaponClass,
                                    weaponClass,
                                    weaponDisplay: resolveWeaponDisplay(weaponClass),
                                    skillClass: null,
                                    sectionKey: champSectionKey,
                                    stageType: stage.type,
                                    stageName: stage.name || stage.type,
                                    isTimeCompetition,
                                    isTimeStage,
                                    championship,
                                    ageGroup: grp,
                                    participants: [],
                                };
                            }
                            allResults[champResultKey].participants.push(participantResult);
                        });
                    });
                });
            }
        });

        Object.keys(allResults).forEach(key => {
            const isTimeComp = allResults[key].isTimeCompetition;
            if (isTimeComp) {
                allResults[key].participants.sort(localUtil.fieldSorter(['resultStatus', 'totalScore', 'name']));
            } else {
                allResults[key].participants.sort(localUtil.fieldSorter(['resultStatus', '-totalScore', '-totalIx', 'name']));
            }
            const uniqueParticipants = new Map();
            allResults[key].participants.forEach(participant => {
                if (isTimeComp) {
                    if (!uniqueParticipants.has(participant.id) || uniqueParticipants.get(participant.id).totalScore > participant.totalScore) {
                        uniqueParticipants.set(participant.id, participant);
                    }
                } else {
                    if (!uniqueParticipants.has(participant.id) || uniqueParticipants.get(participant.id).totalScore < participant.totalScore) {
                        uniqueParticipants.set(participant.id, participant);
                    }
                }
            });
            allResults[key].participants = Array.from(uniqueParticipants.values());
        });

        return allResults;
    }

    // Compute Lagresultater standings from convention.scoreTeams[]: free-form
    // 2-4 member teams assembled by an admin, ranked by summed individual
    // totals within a manually chosen category (open/u/jr/k/m/v55/v65/v73).
    getScoreTeamResults(convention, results, participantsList = [], classMap = [], disciplineMap = []) {
        const scoreTeams = convention?.scoreTeams || [];
        if (!scoreTeams.length) return [];
        const resultList = this.getResultsListForAllClasses(
            convention, results, participantsList, classMap, disciplineMap,
        );
        return buildScoreTeamSections(scoreTeams, resultList);
    }

    getInProgressLags(convention, results, participants = []) {
        if (!convention?.stages?.length) return [];
        const out = [];

        convention.stages.forEach((stage, idx) => {
            const lagIdx = idx + 1;
            const stageParticipants = convention[`participants${lagIdx}`] || [];
            if (stageParticipants.length === 0) return;

            // Pågående = stages that are not yet done. stageClosed = paused (still ongoing),
            // only stageDone means the lag is finished and belongs in Resultatliste.
            if (parseInt(stage.stageDone, 10)) return;

            const expectedStands = expectedStandsForStage(stage, convention);
            const stageResults = results.filter(r => r.stage === stage.md5);

            const rows = stageParticipants.map(p => {
                const pResults = stageResults.filter(r => r.participant === p.id);
                const cells = {};
                let latestUpdate = 0;
                pResults.forEach(r => {
                    cells[r.stand] = r;
                    const t = r.updatedDate ? new Date(r.updatedDate).getTime() : 0;
                    if (t > latestUpdate) latestUpdate = t;
                });
                const current = participants.find(cp => cp.id === p.id) || p;
                const { totalScore, totalIx } = rowTotals(cells, stage.type);
                // Points dropped so far + best still-attainable final score (Sum column).
                const { totalLost, maxPossible } = rowLossSummary(cells, stage, convention, p.class, expectedStands);
                return {
                    participantId: p.id,
                    name: localUtil.displayName(current),
                    firstname: current.firstname || p.firstname || '',
                    lastname: current.lastname || p.lastname || '',
                    sportsClub: current.sportsClub || 'Mangler klubb',
                    weaponClass: p.class || '',
                    weaponAbbr: weaponAbbr(p.class || ''),
                    cells,
                    totalScore,
                    totalIx,
                    totalLost,
                    maxPossible,
                    latestUpdate,
                    filledCount: Object.keys(cells).length,
                    // Still on a maximum score for every stand shot so far → emphasised in the UI.
                    // p.class drives the special-discipline 5-shot cap.
                    isPerfect: isPerfectSoFar(cells, stage, convention, p.class),
                };
            });

            const visibleRows = rows
                .filter(r => r.filledCount > 0)
                .sort((a, b) => {
                    // Full pott (still perfect) shooters are always hoisted to the top.
                    const aPerfect = a.isPerfect ? 0 : 1;
                    const bPerfect = b.isPerfect ? 0 : 1;
                    if (aPerfect !== bPerfect) return aPerfect - bPerfect;
                    // Then incomplete before completed; within each group, newest update first
                    const aIncomplete = a.filledCount < expectedStands ? 0 : 1;
                    const bIncomplete = b.filledCount < expectedStands ? 0 : 1;
                    if (aIncomplete !== bIncomplete) return aIncomplete - bIncomplete;
                    return b.latestUpdate - a.latestUpdate;
                });
            if (visibleRows.length === 0) return;

            // Render at least as many columns as the highest scored stand on this lag,
            // so stands beyond the schema-defined count still show up
            let maxScoredStand = 0;
            visibleRows.forEach(r => {
                Object.keys(r.cells).forEach(k => {
                    const n = parseInt(k, 10);
                    if (n > maxScoredStand) maxScoredStand = n;
                });
            });
            const displayStands = Math.max(expectedStands, maxScoredStand);

            const weaponSet = new Set();
            visibleRows.forEach(r => { if (r.weaponClass) weaponSet.add(r.weaponClass); });
            const weaponDisplays = Array.from(weaponSet).map(w => resolveWeaponDisplay(w));

            const stageTypeLabel = getStageTypeDisplay(stage.type);
            const weaponSuffix = weaponDisplays.length > 1
                ? ` (${weaponDisplays.join(' + ')})`
                : '';
            const headerLabel = `Lag ${lagIdx} — ${stageTypeLabel}${weaponSuffix}`;

            out.push({
                stageMd5: stage.md5,
                idx: lagIdx,
                stage,
                expectedStands,
                displayStands,
                headerLabel,
                weapons: weaponDisplays,
                rows: visibleRows,
            });
        });

        return out;
    }

    getShooterDetail(convention, results, participants = [], { participantId, stageMd5 } = {}, { classMap = [], disciplineMap = [] } = {}) {
        if (!convention?.stages?.length || !participantId || !stageMd5) return null;
        const stage = convention.stages.find(s => s.md5 === stageMd5);
        if (!stage) return null;

        const stageIdx = convention.stages.indexOf(stage);
        const lagIdx = stageIdx + 1;
        const stageParticipants = convention[`participants${lagIdx}`] || [];
        const participant = stageParticipants.find(p => p.id === participantId);
        if (!participant) return null;

        const current = participants.find(cp => cp.id === participantId) || participant;
        const skillClass = resolveSkillClass(participant, current, stage, classMap, disciplineMap);

        const stageResults = results.filter(r => r.stage === stageMd5 && r.participant === participantId);
        const cells = {};
        let totalScore = 0;
        let totalIx = 0;
        let totalHits = 0;
        let totalTargets = 0;
        const perStand = [];

        stageResults.forEach(r => {
            cells[r.stand] = r;
        });

        const sortedStands = Object.keys(cells).map(n => parseInt(n, 10)).sort((a, b) => a - b);
        sortedStands.forEach(standNum => {
            const r = cells[standNum];
            const value = perStandScore(r, stage.type);
            perStand.push({ stand: standNum, value, result: r });
            totalScore += value;
            totalIx += r.scoreInner || 0;
            totalHits += r.scoreHits || 0;
            totalTargets += r.scoreTargets || 0;
        });

        return {
            participantId,
            participantMd5: participant.md5,
            conventionId: convention.id,
            firstname: current.firstname || participant.firstname || '',
            lastname: current.lastname || participant.lastname || '',
            name: localUtil.displayName(current),
            sportsClub: current.sportsClub || 'Mangler klubb',
            weaponClass: participant.class || '',
            weaponDisplay: resolveWeaponDisplay(participant.class || ''),
            weaponAbbr: weaponAbbr(participant.class || ''),
            skillClass,
            stage,
            stageMd5,
            stageType: stage.type,
            stageTypeDisplay: getStageTypeDisplay(stage.type),
            lagIdx,
            expectedStands: expectedStandsForStage(stage, convention),
            perStand,
            cells,
            totalScore,
            totalIx,
            aggregates: {
                scoreHits: totalHits,
                scoreTargets: totalTargets,
                scoreInner: totalIx,
            },
        };
    }

    totalScore({ participant, convention, stage, isFieldShooting }) {
        const participantResults = this.conventionResults.filter(result => result.participant === participant.id
            && result.stage === stage.md5
            && result.convention === convention.id
        );
        let totalScore = 0;
        let totalIx = 0;

        if (this.isT96(stage.type)) {
            T96_SERIES.forEach(series => {
                const stand = series.idx;
                const r = participantResults.find(res => res.stand === stand);
                const seriesScore = (r?.scoreHits || 0) + (r?.scoreTargets || 0) + (r?.scoreInner || 0);
                totalScore += seriesScore;
                totalIx    += r?.scoreInner || 0;
            });
            return { totalScore, totalIx };
        }

        let standScore = 0;
        if (isFieldShooting) {
            this.generateArray(stage.stages || 10).map(stand => {
                standScore = 0;
                const targetResult = participantResults.find(result => result.stand === stand);
                standScore += targetResult?.scoreHits || 0;
                standScore += targetResult?.scoreTargets || 0;
                if (targetResult?.scorePenalty) {
                    standScore -= targetResult.scorePenalty;
                }
                totalScore += standScore;
                totalIx += targetResult?.scoreInner || 0;
            });
        } else {
            this.getParticipantFields(stage).map((field, index) => {
                const targetResult = participantResults.find(result => result.targetIndex === index);
                if (targetResult) {
                    totalScore += targetResult.scoreTotal;
                }
            });
        }

        return { totalScore, totalIx };
    }
}

const store = new ConventionResultStore();
export default store;
